import React, { useMemo, useState, useEffect } from 'react';
import { formatCashFlowPeriods } from 'views/ProFormaTable/helpers';
import ProFormaGenericTable from 'views/ProFormaTable/components/ProFormaGenericTable/ProFormaGenericTable';
import { IOptionAnyValue } from 'bricks/types';
import { useScrollableSync } from 'views/ProFormaTable/hooks/useScrollableSync';
import { ProFormaGenericColumn } from 'views/ProFormaTable/components/ProFormaGenericTable/types';
import { useProFormaSocketContext } from '../../../ProForma/socketContext/ProFormaSocketProvider';
import { IContributionsAndDistributionsProps } from './types';
import {
    DISTRIBUTIONS_COLUMNS, CASH_FLOW_COLUMNS, getWaterfallColumns,
    CONTRIBUTIONS_COLUMNS,
} from './constants';

const ContributionsAndDistributions = ({
    waterfallType,
    contributionsRows,
    contributionsTotals,
    distributionWaterfallRows,
    updateDistributionWaterfallRow,
    updateContributionRow,
    distributionsRows,
    distributionsTotals,
    netCashFlowRows,
    netCashFlowTotals,
}: IContributionsAndDistributionsProps) => {
    const {
        waterfallAnalysis,
        handleUpdateDistributionWaterfall,
        cashFlowPeriodsByCadence,
        periodCadence,
        proForma,
    } = useProFormaSocketContext();

    const currentHurdleMetric = useMemo(() => {
        const hurdleMetricType = waterfallType === 'multi_gp'
            ? waterfallAnalysis?.gpGpDistributionWaterfall?.hurdleMetricType
            : waterfallAnalysis?.distributionWaterfall?.hurdleMetricType;

        return {
            value: hurdleMetricType || 'irr',
            label: (hurdleMetricType === 'equity_multiple' ? 'EMx' : 'IRR'),
        };
    }, [waterfallType, waterfallAnalysis]);

    useEffect(() => {
        setSelectedIRR(currentHurdleMetric);
    }, [currentHurdleMetric]);

    const [selectedIRR, setSelectedIRR] = useState<IOptionAnyValue>(currentHurdleMetric);

    const handleHurdleMetricChange = (option: IOptionAnyValue) => {
        setSelectedIRR(option);
        handleUpdateDistributionWaterfall(waterfallType, {
            hurdleMetricType: option.value,
        });
    };

    const periodColumns: ProFormaGenericColumn[] = useMemo(() => {
        const periods = formatCashFlowPeriods(cashFlowPeriodsByCadence, periodCadence, !proForma.saleDate || !proForma.startDate);
        return periods?.map((periodInfo, index) => ({
            key: `column${index + 1}`,
            field: `column${index + 1}`,
            header: (
                <div>
                    <div className="h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-secondary text-right mb-0">
                        {periodInfo.period}
                    </div>
                    <div className="h6 text-uppercase text-right text-nowrap mt-1">{periodInfo.label}</div>
                </div>
            ),
            format: 'currency' as const,
            isFixed: false,
        })) || [];
    }, [cashFlowPeriodsByCadence, periodCadence, proForma.saleDate, proForma.startDate]);

    const WATERFALL_COLUMNS = useMemo(
        () => getWaterfallColumns(selectedIRR, handleHurdleMetricChange),
        [selectedIRR, waterfallAnalysis],
    );

    const { handleScroll, handleScrollableRef } = useScrollableSync();

    return (
        <div className="w-100">
            <ProFormaGenericTable
                columns={[...CONTRIBUTIONS_COLUMNS, ...periodColumns]}
                rows={contributionsRows}
                setRows={() => { }}
                onScrollableRef={handleScrollableRef(0)}
                onScroll={handleScroll}
                handleUpdateRow={updateContributionRow}
                hideAddButton
                totalValues={contributionsTotals}
                fixedColumnsMaxWidth="720px"
            />
            <div className="pt-1" />
            <ProFormaGenericTable
                firstColumnWidth="160px"
                columns={[...WATERFALL_COLUMNS, ...periodColumns]}
                rows={distributionWaterfallRows}
                setRows={() => { }}
                onScrollableRef={handleScrollableRef(1)}
                handleUpdateRow={updateDistributionWaterfallRow}
                onScroll={handleScroll}
                hideAddButton
                hideTotalFooter
                fixedColumnsMaxWidth="720px"
            />
            <div className="pt-1" />
            <ProFormaGenericTable
                columns={[...DISTRIBUTIONS_COLUMNS, ...periodColumns]}
                rows={distributionsRows}
                setRows={() => { }}
                onScrollableRef={handleScrollableRef(2)}
                onScroll={handleScroll}
                hideAddButton
                totalValues={distributionsTotals}
                fixedColumnsMaxWidth="720px"
            />
            <div className="pt-1" />
            <ProFormaGenericTable
                columns={[...CASH_FLOW_COLUMNS, ...periodColumns]}
                rows={netCashFlowRows}
                setRows={() => { }}
                onScrollableRef={handleScrollableRef(3)}
                onScroll={handleScroll}
                hideAddButton
                totalValues={netCashFlowTotals}
                fifthColumnWidth="140px"
                fixedColumnsMaxWidth="720px"
            />
        </div>
    );
};

export default ContributionsAndDistributions;
